import React from "react";
import { RESTORE_POLICY_FIXED_SECTIONS } from "../constants/restorePolicyFixedSections";
import type { BCPSectionConfig } from "./bcpFieldConfig";

export const RESTORE_POLICY_SECTIONS: BCPSectionConfig[] = [
  {
    id: "basic_info",
    title: "基本情報",
    fields: [
      {
        id: "company_info",
        label: "企業の基本情報",
        type: "textarea",
        minHeight: "medium",
        placeholder: "企業名：\n所在地：\n従業員数：\n事業内容：",
        aiConsult: false,
        required: true,
      },
    ],
  },
  {
    id: "bcp_goals",
    title: "1. BCP目標設定",
    fields: [
      {
        id: "disaster_risks",
        label: "1-1 想定されるリスク",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "当社所在地で発生しうる災害リスク（ハザードマップより）\n\n地震：可能性 {高中低}\n　被害想定：{内容}\n\n津波：可能性 {高中低}\n　被害想定：{内容}\n\n洪水・高潮： 可能性 {高中低}\n　被害想定：{内容}\n\n土砂災害（地滑り等）： 可能性 {高中低}\n　被害想定：{内容}\n\n液状化：可能性 {高中低}\n　被害想定：{内容}",
        guide: {
          content: (
            <div className="space-y-2">
              <div style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                <p>所在地の想定リスクは、国や自治体等が制作しているハザードマップから確認できます。</p>
                <p>SONAE-AIでは公開データに基づいて参考情報を提供していますが、ご自身でもハザードマップで確認いただくことを推奨します。</p>
                <p className="mt-2">【参考】</p>
                <p>
                  ハザードマップポータルサイト{' '}
                  <a
                    href="https://disaportal.gsi.go.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://disaportal.gsi.go.jp/
                  </a>
                </p>
                <p>
                  東京都被害想定ホームページ{' '}
                  <a
                    href="https://www.higaisoutei.metro.tokyo.lg.jp/mydmgpred.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://www.higaisoutei.metro.tokyo.lg.jp/mydmgpred.html
                  </a>
                </p>
                <p>
                  J-SHIS 地震ハザードステーション{' '}
                  <a
                    href="https://www.j-shis.bosai.go.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://www.j-shis.bosai.go.jp/
                  </a>
                </p>
              </div>
            </div>
          ),
          example: <div></div>,
        },
        aiConsult: true,
      },
      {
        id: "restore_policy_important_business",
        label: "1-2 重要事業と目標復旧時間（RTO: Recovery Time Objective）",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n理由：\n\n・顧客への迷惑（影響）が出るまでの期間：\n・社会全体への影響が出るまでの期間：\n・顧客離れが発生するまでの期間：\n・財務への影響（資金枯渇など）が出るまでの期間：\n\n⇒上記を踏まえた目標復旧時間（RTO）：\n\n【重要事業2：{事業名}】\n理由：\n\n・顧客への迷惑（影響）が出るまでの期間：\n・社会全体への影響が出るまでの期間：\n・顧客離れが発生するまでの期間：\n・財務への影響（資金枯渇など）が出るまでの期間：\n\n⇒上記を踏まえた目標復旧時間（RTO）：",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                目標復旧時間（RTO）設定の考え方
              </p>
              <p>
              1．選定した重要事業が中断した際に、以下の影響が出始めると思われる事業中断期間（24時間、1週間、1ヶ月等）を記入してください。< br />
              ・顧客への迷惑　　⇒ 「重要顧客を含む多数の顧客に迷惑」< br/>
              ・社会全体への迷惑 ⇒ 「確実に社会全体に迷惑をかける」< br/>
              ・顧客離れの可能性 ⇒ 「重要顧客を含む多数の顧客離れが発生」< br/>
              ・財務影響 　　　　⇒ 「手持ち資金の枯渇の発生」< br/>
              2．上記の影響が出始めると思われる事業中断期間で、一番期間の短いものを目標復旧時間とします。< br/>
              </p>
            </div>
          ),
          example: <div></div>,
        },
        aiConsult: true,
        required: true,
      },
    ],
  },
  {
    id: "resource_strategy",
    title: "2. 経営資源と現地復旧戦略整理",
    fields: [
      {
        id: "human_resources",
        label: "2-1 重要事業の実施に必要な人的リソース",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                経営資源と現地復旧戦略の考え方
              </p>
              <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                選定した重要事業の機能ごとに必要な経営資源を整理するとともに、その経営資源が使用できない場合の現地復旧戦略（代替策および回避策）を検討します。
              </p>
            </div>
          ),
          example: (
            <div>
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  color: '#4B5563',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                【重要事業1：建築資材の法人向け販売】
                {"\n"}・法人営業担当（顧客対応、提案営業、受発注管理）
                {"\n"}・在庫管理担当（資材管理、出庫対応）
                {"\n"}・物流管理担当（配送計画、輸送手配）
                {"\n"}※経営資源が使用できない場合の代替策：他部署の応援要員を活用、他社要員を活用
                {"\n"}※経営資源を使用できるように平時から実施する回避策：スキル保有者の増員、スキルの平準化、要員の増員
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
      {
        id: "facility_recovery",
        label: "2-2 重要事業の実施に必要な設備・インフラ",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                経営資源と現地復旧戦略の考え方
              </p>
              <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                選定した重要事業の機能ごとに必要な経営資源を整理するとともに、その経営資源が使用できない場合の現地復旧戦略（代替策および回避策）を検討します。
              </p>
            </div>
          ),
          example: (
            <div>
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  color: '#4B5563',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                【重要事業1：最重要顧客（A社）の建設現場対応】
                {"\n"}・現場施工に必要な重機（クレーン、フォークリフト）
                {"\n"}・専門工具および測定機器（配管検査装置、溶接機など）
                {"\n"}・仮設事務所および作業現場間の通信設備
                {"\n"}※経営資源が使用できない場合の代替策：設備等を再調達
                {"\n"}※経営資源を使用できるように平時から実施する回避策：設備などの耐震対策、敷地・建物への浸水対策
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
      {
        id: "it_recovery",
        label: "2-3 重要事業の実施に必要なシステム",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                経営資源と現地復旧戦略の考え方
              </p>
              <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                選定した重要事業の機能ごとに必要な経営資源を整理するとともに、その経営資源が使用できない場合の現地復旧戦略（代替策および回避策）を検討します。
              </p>
            </div>
          ),
          example: (
            <div>
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  color: '#4B5563',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                【重要事業1：産業用機械部品の製造・販売】
                {"\n"}・受発注管理システム（法人顧客の注文管理）
                {"\n"}・在庫管理システム（リアルタイム在庫状況の確認）
                {"\n"}・顧客管理システム（営業履歴、契約情報の管理）
                {"\n"}※経営資源が使用できない場合の代替策：別システムで代用、システムを使わない業務フロー等
                {"\n"}※経営資源を使用できるように平時から実施する回避策：サーバーの二重化、クラウド化、サーバーの耐震対策等
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
      {
        id: "supply_chain",
        label: "2-4 重要事業の実施に必要な委託先／関係先",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                経営資源と現地復旧戦略の考え方
              </p>
              <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: '20px' }}>
                選定した重要事業の機能ごとに必要な経営資源を整理するとともに、その経営資源が使用できない場合の現地復旧戦略（代替策および回避策）を検討します。
              </p>
            </div>
          ),
          example: (
            <div>
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  color: '#4B5563',
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                【重要事業1：公共施設向け設備・システム受注】
                {"\n"}・資材供給業者（建築資材の仕入先）
                {"\n"}・物流業者（配送業務の外部委託先）
                {"\n"}・ITサービス提供業者（システムの保守・運用支援）
                {"\n"}※経営資源が使用できない場合の代替策：取引のない委託先・関係先との取引等
                {"\n"}※経営資源を使用できるように平時から実施する回避策：委託先・関係先の二重化、委託先・関係先のBCP整備等
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
      {
        id: "financial_plan",
        label: "2-5 事業継続のための財務計画",
        type: "textarea",
        minHeight: "medium",
        defaultValue: RESTORE_POLICY_FIXED_SECTIONS.FINANCIAL_PLAN_2_5,
        aiConsult: true,
      },
    ],
  },
  {
    id: "alternative_strategy",
    title: "3. 現地復旧戦略以外の戦略",
    fields: [
      {
        id: "backup_location",
        label: "3-1 バックアップ拠点の確保",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "remote_work",
        label: "3-2 業務のリモート対応計画",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "external_partners",
        label: "3-3 外部業者・パートナーとの提携",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "inventory_utilization",
        label: "3-4 在庫活用",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【在庫活用】\n・重要製品の供給を継続するため、在庫を事前に積み増ししておく。\n・複数の倉庫に分散保管し、リスクを低減する。\n\n※在庫活用が該当しない事業の場合は、記載不要です。",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                代替策の考え方
              </p>
              <div style={{ color: '#4B5563' }}>
                重要事業において現地復旧戦略が困難な場合に、とりうる現地復旧戦略以外の戦略を策定してください。
                {"\n"}
                戦略の例として「バックアップ拠点の確保」「業務のリモート対応計画」「外部業者・パートナーとの提携」「在庫活用」などがあります。
              </div>
            </div>
          ),
          example: (
            <div>
              <p
                className="whitespace-pre-wrap"
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  width: '100%',
                  maxWidth: '1151px',
                  minHeight: '360px',
                  color: '#4B5563',
                }}
              >
                【バックアップ拠点の確保】
                {"\n"}・主要拠点の復旧に時間を要する場合、別拠点で同等の業務を実施する。
                {"\n"}・流用可能な設備はそのまま使用し、特有の設備は必要に応じて移設・手配する。
                {"\n\n"}【業務のリモート対応計画】
                {"\n"}・管理部門が災害時でも業務を継続できるよう、在宅勤務環境を整備する。
                {"\n"}・VPNやクラウドサービスなどのITインフラを導入する。
                {"\n\n"}【外部業者・パートナーとの提携】
                {"\n"}・有事には外部の協力会社に生産・業務を委託する。
                {"\n"}・事前協定を結び、必要に応じて自社スタッフを派遣して技術・運用面を支援する。
                {"\n\n"}【在庫活用】
                {"\n"}・重要製品の供給を継続するため、在庫を事前に積み増ししておく。
                {"\n"}・複数の倉庫に分散保管し、リスクを低減する。
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
    ],
  },
];

export type RestorePolicyFieldId =
  (typeof RESTORE_POLICY_SECTIONS)[number]["fields"][number]["id"];
